import { FC, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Grid,
  Typography,
  useTheme,
  TextField,
  Button,
  IconButton,
  styled,
  InputAdornment,
  useMediaQuery,
} from "@mui/material";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import PermContactCalendarIcon from "@mui/icons-material/PermContactCalendar";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useForm, Controller } from "react-hook-form";
import { Link } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { useApp } from "../../hooks/useApp";
import { loginTypes } from "../../types/Login";
import { SVloginCodeRequest, SVloginRequest } from "../../services/Security";
import Swal from "sweetalert2";

const LinkTo = styled(Link)(({ theme }) => ({
  color: theme.palette.secondary.main,
  textDecoration: "none",
  fontWeight: 700,
}));

const Login: FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const {
    authInfo,
    isLoading,
    setLoading,
    setErrorMsg,
    modalData,
    setModalData,
    successMsg,
    setSuccessMsg,
    errorMsg,
    loginProvider,
    resetErrorMsg,
    resetSuccessMsg,
  } = useApp();
  const [showCode, setShowCode] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState(false);
  const isSm = useMediaQuery(theme.breakpoints.down("md"));

  const validation = Yup.object().shape({
    username: Yup.string().required("Campo es requerido"),
    password: Yup.string().required("Contraseña es requerida"),
    code: Yup.string(),
  });

  const defaultValues: loginTypes = {
    username: "",
    password: "",
    code: "",
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    watch,
    setError,
  } = useForm<loginTypes>({
    defaultValues,
    resolver: yupResolver(validation),
  });
  const watch_code = watch("code");

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleLogin = async (data: loginTypes) => {
    try {
      setLoading(true);
      //Get two factor code
      if (!showCode) {
        let request: any = await SVloginCodeRequest(data);
        setLoading && setLoading(false);
        const needCode = request?.user?.needCode;
        if (request?.role?.id === 3) {
          Swal.fire({
            icon: "error",
            title: "Acceso denegado",
            text: "Debe iniciar sesión en la aplicación, ya que no tiene permiso para acceder al portal web como mensajero",
            confirmButtonColor: "#00800A",
          });
          return;
        }

        if (!needCode) {
          setLoading && setLoading(true);
          // Directly proceed with login request
          const loginData = await SVloginRequest(data);
          await loginProvider(loginData);
          return;
        }
        setShowCode(true);
        //setSuccessMsg && setSuccessMsg(request?.message  );
        setSuccessMsg &&
          setSuccessMsg(
            request?.status === 200
              ? request?.message
              : //? `${t("sendCodeMsg") + data.username}`
                request?.message
          );
        return;
        //Código enviado al correo ana.vivas@bitcode-enterprise.com
      }
      if (showCode && watch_code === "") {
        setError("code", { type: "custom", message: "Campo es requerido" });
        setLoading(false);
        return;
      }
      //Authenticate user
      const loginData = await SVloginRequest(data);
      await loginProvider(loginData);
      setLoading && setLoading(false);
    } catch (error: any) {
      setLoading(false);
      setErrorMsg(error.message);
    }
  };

  const onSubmit = async (data: any) => {
    await handleLogin(data);
  };

  return (
    <>
      <form>
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <Controller
              name={"username"}
              control={control}
              render={() => (
                <TextField
                  fullWidth
                  size="small"
                  type="email"
                  label="Usuario"
                  autoComplete="username"
                  disabled={showCode}
                  {...register("username")}
                  error={errors.username && Boolean(errors.username)}
                  helperText={errors.username && errors.username.message}
                  sx={{
                    "& .MuiInput-root:before": {
                      borderBottomColor: "#8bc8bc!important",
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start" sx={{ ml: 1 }}>
                        <PermContactCalendarIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name={"password"}
              control={control}
              render={() => (
                <TextField
                  fullWidth
                  size="small"
                  label="Contraseña"
                  type={showPassword ? "text" : "password"}
                  disabled={showCode}
                  {...register("password")}
                  error={errors.password && Boolean(errors.password)}
                  sx={{
                    "& .MuiInput-root:before": {
                      borderBottomColor: "#8bc8bc!important",
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start" sx={{ ml: 1 }}>
                        <LockOutlinedIcon />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="start">
                        <IconButton onClick={handleClickShowPassword}>
                          {showPassword ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} hidden={!showCode}>
            <Controller
              name={"code"}
              control={control}
              render={() => (
                <TextField
                  fullWidth
                  label={"Código"}
                  size="small"
                  type="text"
                  {...register("code")}
                  error={!!errors.code}
                  helperText={!!errors.code && errors.code?.message}
                  inputProps={{ autoComplete: "off" }}
                  sx={{
                    "& .MuiInput-root:before": {
                      borderBottomColor: "#8bc8bc!important",
                    },
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Button
              variant="contained"
              fullWidth
              onClick={handleSubmit(onSubmit)}
              sx={{
                mt: 3,
              }}
            >
              Iniciar sesión
            </Button>
          </Grid>
        </Grid>
      </form>
      <Typography align="right" sx={{ mt: 6 }} variant="body1">
        <LinkTo to="rememberpassword">¿Olvidaste tu contraseña?</LinkTo>
      </Typography>
      {/* <Typography align="center" sx={{ mt: 12 }} variant="body1">
        ¿No tienes cuenta? <LinkTo to="rememberpassword">Crea una</LinkTo>
      </Typography> */}
    </>
  );
};

export default Login;
